import { createImage } from './cropImg';

type ResizeOptions = {
  maxWidth: number;
  maxHeight: number;
  mimeType?: string;
  quality?: number;
  fileName?: string;
  targetMaxBytes?: number;
};

export async function resizeImageFile(
  inputFile: File | string,
  options: ResizeOptions
): Promise<File> {
  const {
    maxWidth,
    maxHeight,
    mimeType = 'image/jpeg',
    quality = 0.92,
    fileName = 'Resized Sample',
    targetMaxBytes,
  } = options;

  async function fileToDataURL(file: File) {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  let originalFile: File | undefined;
  let dataUrl: string;
  if (typeof inputFile === 'string') {
    dataUrl = inputFile;
  } else {
    originalFile = inputFile;
    dataUrl = await fileToDataURL(inputFile);
    if (targetMaxBytes && originalFile.size <= targetMaxBytes) {
      return originalFile;
    }
  }

  const img = await createImage(dataUrl);

  const { width: newW0, height: newH0 } = getContainSize(
    img.width,
    img.height,
    maxWidth,
    maxHeight
  );
  const newW = Math.min(newW0, img.width);
  const newH = Math.min(newH0, img.height);

  // create working canvas
  let canvas = document.createElement('canvas');
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  async function canvasToBlob(canvasEl: HTMLCanvasElement, type: string, q?: number) {
    return new Promise<Blob>((resolve, reject) => {
      canvasEl.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Failed to create blob'));
          resolve(blob);
        },
        type,
        q
      );
    });
  }

  if (!targetMaxBytes) {
    const outBlob = await canvasToBlob(canvas, mimeType, quality);
    return new File([outBlob], fileName, { type: mimeType, lastModified: Date.now() });
  }

  let outBlob = await canvasToBlob(
    canvas,
    mimeType,
    mimeType === 'image/png' ? undefined : quality
  );

  if (outBlob.size <= targetMaxBytes) {
    return new File([outBlob], fileName, { type: mimeType, lastModified: Date.now() });
  }

  const isCompressible = mimeType === 'image/jpeg' || mimeType === 'image/webp';

  if (isCompressible) {
    let lo = 0.35;
    let hi = Math.min(quality, 0.98);
    let bestBlob: Blob | null = null;
    let bestQuality = lo;

    const tryBlob = await canvasToBlob(canvas, mimeType, lo);
    if (tryBlob.size <= targetMaxBytes) {
      let iter = 0;
      while (iter < 8 && hi - lo > 0.01) {
        const mid = (lo + hi) / 2;
        const b = await canvasToBlob(canvas, mimeType, mid);
        if (b.size <= targetMaxBytes) {
          bestBlob = b;
          bestQuality = mid;
          lo = mid;
        } else {
          hi = mid;
        }
        iter++;
      }
      outBlob = bestBlob ?? tryBlob;
      if (outBlob.size <= targetMaxBytes) {
        return new File([outBlob], fileName, { type: mimeType, lastModified: Date.now() });
      }
    }
  }

  let currentW = canvas.width;
  let currentH = canvas.height;
  const minDimension = 64;
  let attempts = 0;

  while (
    outBlob.size > targetMaxBytes &&
    attempts < 10 &&
    currentW > minDimension &&
    currentH > minDimension
  ) {
    attempts++;
    const scale = Math.sqrt(targetMaxBytes / outBlob.size) || 0.8;
    const appliedScale = Math.max(0.5, Math.min(0.95, scale));
    currentW = Math.max(minDimension, Math.floor(currentW * appliedScale));
    currentH = Math.max(minDimension, Math.floor(currentH * appliedScale));

    const newCanvas = document.createElement('canvas');
    newCanvas.width = currentW;
    newCanvas.height = currentH;
    const newCtx = newCanvas.getContext('2d');
    if (!newCtx) break;
    newCtx.drawImage(img, 0, 0, currentW, currentH);

    const tryQ = isCompressible ? Math.max(0.45, Math.min(quality, 0.9)) : undefined;
    outBlob = await canvasToBlob(newCanvas, mimeType, tryQ as number | undefined);

    canvas = newCanvas;
  }

  return new File([outBlob], fileName, { type: mimeType, lastModified: Date.now() });
}

function getContainSize(originalW: number, originalH: number, maxW: number, maxH: number) {
  const ratio = Math.min(maxW / originalW, maxH / originalH);

  return {
    width: Math.round(originalW * ratio),
    height: Math.round(originalH * ratio),
  };
}
